import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { browserHistory } from 'react-router';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import 'yup-phone';

import {
  ModalOverlay,
  ResponsiveContent,
  ModalCloseHeader
} from '@evdy/web-core/dist/components/shared/Modal2020';
import TextInput from '../TextInput';

import { webCommunityOperations } from '@evdy/web-redux/dist/lib/webCommunity';

import styles from './AnonymousDetailsModal.module.scss';

const { stageMessageValues, leaveMessage } = webCommunityOperations;

const {
  'anonymous-details-modal': anonymousDetailsModalClass,
  'anonymous-details-body': anonymousDetailsBodyClass,
  'privacy-policy': privacyPolicyClass,
  'privacy-link': privacyLink,
  'button-container': buttonContainerClass,
  inputs: inputsClass
} = styles;

const AnonymousDetailsModal = ({ baseRoute, closeModal }) => {
  const { user, memorialId, messageStaging } = useSelector(({ authUser, webCommunity }) => ({
    memorialId: webCommunity.inviteMeta.data?._id,
    user: authUser?.data,
    messageStaging: webCommunity.messageStaging
  }));
  const dispatch = useDispatch();

  const userHasPhone = !!user?.phone || !!user?.meta?.posterShellPhone;
  const { messageSaved } = messageStaging;

  // TODO: see if we can consolidate Yup schema validation post-MVP
  // need to figure out non required phone field but validate with yup-phone
  const emailRegex = /^\S+@\S+\.\S+$/;

  const AnonymousDetailsFormSchema = Yup.object().shape({
    name: Yup.string().required('This is required!'),
    phone: Yup.string().test('is-valid', 'Please enter a valid phone number', value =>
      value
        ? Yup.string()
            .phone('US', true, 'Please enter a valid phone number.')
            .isValidSync(value)
        : true
    ),
    email: Yup.string()
      .matches(emailRegex, 'Please provide a valid email')
      .required('Please enter your email')
  });

  const saveMessage = ({ name, email, phone }) => {
    const strippedPhone = phone?.replace(/\D+/g, '');
    dispatch(
      leaveMessage({
        memorialId,
        name,
        email,
        phone: strippedPhone,
        message: messageStaging.message
      })
    );
  };

  const onSubmit = ({ name, email, phone }) => {
    saveMessage({ name, email, phone });
    dispatch(stageMessageValues({ phone }));
  };

  // if message is sent and user does not have a phone number
  // then open the add phone modal, else finish flow
  useEffect(() => {
    if (messageSaved) {
      if (!userHasPhone) {
        browserHistory.push({ pathname: `${baseRoute}/condolence/add-phone` });
      } else {
        // finish message post flow
        browserHistory.push({ pathname: `${baseRoute}/condolence/message-posted` });
      }
    }
  }, [baseRoute, messageSaved, userHasPhone]);

  const PrivacyPolicySection = () => (
    <div className={privacyPolicyClass}>
      By continuing, you agree to our{' '}
      <a href="/privacy" rel="noreferrer noopener" target="_blank" className={privacyLink}>
        Privacy Policy
      </a>{' '}
      and{' '}
      <a href="/terms" rel="noreferrer noopener" target="_blank" className={privacyLink}>
        Terms & Conditions
      </a>
    </div>
  );

  return (
    <ModalOverlay {...{ closeModal }} modalVisible customClass={anonymousDetailsModalClass}>
      <ResponsiveContent>
        <ModalCloseHeader {...{ closeModal }} />
        <div className={anonymousDetailsBodyClass}>
          <h3>Please add your details below</h3>
          <p>Include your name along with your message so friends and family know who it's from</p>
          <Formik
            initialValues={{ name: '', phone: '', email: '' }}
            validationSchema={AnonymousDetailsFormSchema}
            onSubmit={onSubmit}
          >
            <Form>
              <div className={inputsClass}>
                <TextInput
                  type="text"
                  label="Full Name (required)"
                  name="name"
                  placeholder="John Smith"
                />
                <TextInput type="tel" label="Phone" name="phone" placeholder="e.g. 123-555-1212" />
                <TextInput
                  type="email"
                  label="Email"
                  name="email"
                  placeholder="johnsmith@email.com"
                />
              </div>
              <div className={buttonContainerClass}>
                <button type="submit">Save</button>
              </div>
            </Form>
          </Formik>
        </div>
        <PrivacyPolicySection />
      </ResponsiveContent>
    </ModalOverlay>
  );
};

export default AnonymousDetailsModal;
